import React, { useRef, useState } from "react";
import { Camera, Upload, X, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * ImageUploader — a phone-camera-friendly image picker.
 * Props:
 *   value: current image URL (string or empty)
 *   onChange: fn(url) called with the uploaded image URL
 *   label: optional field label
 *   testId: base data-testid (defaults to "image-uploader")
 *   variant: "square" (default) | "banner" (16:9)
 *   accent: color used for the button (defaults to orange)
 */
export function ImageUploader({ value, onChange, label, testId = "image-uploader", variant = "square", accent = "#FF6B00", lang = "en", maxWidth }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const isBanner = variant === "banner";
  const aspect = isBanner ? "aspect-[16/9]" : "aspect-square";

  const upload = async (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      setErr(lang === "ar" ? "الحجم أكبر من 5 ميجا" : "File too large (max 5 MB)");
      return;
    }
    setErr(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch (e) {
      setErr(e?.response?.data?.detail || (lang === "ar" ? "فشل الرفع" : "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const preview = value;

  return (
    <div className="space-y-2" data-testid={testId} style={maxWidth ? { maxWidth } : {}}>
      {label && <div className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)]">{label}</div>}

      <div className={`relative ${aspect} rounded-xl border-2 border-dashed overflow-hidden ${preview ? "border-transparent" : "border-[var(--so-border)]"}`} style={preview ? {} : { background: "var(--so-surface, rgba(0,0,0,0.03))" }}>
        {preview ? (
          <>
            <img src={preview} alt="" className="w-full h-full object-cover" data-testid={`${testId}-preview`} />
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); onChange(""); }}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-red-600"
              data-testid={`${testId}-remove`}
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : uploading ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-[var(--so-text-soft)] gap-2">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: accent }} />
            <span className="text-xs">{lang === "ar" ? "جاري الرفع..." : "Uploading..."}</span>
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-4">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); cameraRef.current?.click(); }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-sm font-semibold"
                style={{ background: accent }}
                data-testid={`${testId}-camera`}
              >
                <Camera className="w-4 h-4" /> {lang === "ar" ? "الكاميرا" : "Camera"}
              </button>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); fileRef.current?.click(); }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border-2"
                style={{ borderColor: accent, color: accent }}
                data-testid={`${testId}-gallery`}
              >
                <Upload className="w-4 h-4" /> {lang === "ar" ? "الاستوديو" : "Gallery"}
              </button>
            </div>
            <div className="text-[11px] text-[var(--so-text-soft)] text-center max-w-[220px]">
              {lang === "ar" ? "JPG · PNG · WEBP — حتى 5 ميجا" : "JPG · PNG · WEBP — up to 5 MB"}
            </div>
          </div>
        )}
      </div>

      {/* Hidden inputs: `capture` opens the camera app on mobile, plain input opens the gallery/file picker */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        capture="environment"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={`${testId}-camera-input`}
      />
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={`${testId}-file-input`}
      />

      {err && <div className="text-xs text-red-500" data-testid={`${testId}-error`}>{err}</div>}
    </div>
  );
}
